// The call window's light host. It reads its own session, config, call and event stream over the shared same-origin
// cookie, and runs the existing CoachPoller and LiveTranscriptPoller, but it never mounts the workspace: no phone
// ownership, no device, no call engine. So a call window can never take the phone, even when the main window
// reloads, and it never runs the call-link redirect (Chromium copies sessionStorage into a window opened without
// noopener). It only listens on 'sidetone-session' and never posts there: the main app reboots on any message.
import {
  automaticDispositionFor, type AutomaticDisposition, ASK_PROVIDERS, CALL_SUMMARY_OUTCOMES, CALL_SUMMARY_SKIPPED_REASONS, type Call, type CallContextCard, type CallDispositionReceipt, type CallSummary,
  type CoachAskAnswer, type CoachFeedbackTarget, type CoachFeedbackVote, type Config, type EventEnvelope, type Session,
} from '@bcio-voice/contracts';
import { ApiClient, type DataEnvelope, isAbortError, isApiRequestError } from '../../lib/api.ts';
import { capability } from '../../lib/capabilities.ts';
import { EventStream, type StreamState } from '../../lib/events.ts';
import { contextCardOf, decideEvent, upsertCall, type CallWithContext } from '../../call/callStore.ts';
import { createControlHandler, MediaAccess } from '../../state/mediaAccess.ts';
import {
  COACH_STORAGE_KEY, CoachAskClient, CoachPoller, type CoachQaEntry, type CoachState, coachAskErrorView, coachDefaultEnabled,
  coachQaReducer, coachUserKey, coachVisible, isRepOnlyQuickAction, readCoachAskResponse,
} from '../../call/coach/coach.ts';
import { UNREADABLE_ANSWER, cleanQuestion } from '../transcript/ask.ts';
import {
  LiveTranscriptPoller, type LiveTranscriptState, liveTranscriptVisible, notifyTranscriptEvent, notifyTranscriptResync,
} from '../transcript/liveTranscript.ts';
import {
  type PreviousCardResult, cardExpected, contextCardModel, dispositionRequest, previousCardPath, previousCards,
} from '../phone/contextCard.ts';
import {
  CALL_WINDOW_LOCK, type CallWindowTarget, type ChannelLike, browserChannel, callWindowAccount, callWindowPath, isCallId,
  readCallWindowMessage,
} from './callWindowLaunch.ts';
import {
  type CallWindowPhase, type DisplayedText, NO_DISPLAYED, type SayPin, effectiveState, nextDisplayedText, pickCallWindowCall, pinCleared,
  selfSelectPending, wrapUpExpected,
} from './callWindowModel.ts';
import { CoachFeedbackVotes } from './feedbackClient.ts';
import { loadWrapUp, wrapUpQuietSkip } from './WrapUp.tsx';

/** The two quick actions that stay on screen (Allen, October 30, 2026); their sayable answer is pinned into say-next. */
export const PINNED_ACTIONS: ReadonlySet<string> = new Set(['Break the ice', 'Say something funny']);
export const HELLO_EVERY_MS = 1000;
export const HELLO_TRIES = 30;
const ASK_ENTRIES = 5;

export interface CallWindowSnapshot {
  phase: CallWindowPhase;
  target: CallWindowTarget;
  session: Session | null;
  config: Config | null;
  call: Call | null;
  card: CallContextCard | null;
  previous: PreviousCardResult | null;
  stream: StreamState;
  failMessage: string | null;
  coachEnabled: boolean;
  coach: CoachState;
  transcript: LiveTranscriptState;
  /** The last lines before Hold, shown muted while held. */
  frozen: string[] | null;
  pin: SayPin | null;
  askBusy: string | null;
  askStatus: string | null;
  askEntries: CoachQaEntry[];
  mediaReady: boolean;
  mediaEpoch: number;
  /** The CRM disposition chosen in this window: the choice, the one being saved, and what happened to the last save. */
  disposition: DispositionState;
  /** Per transcript segment, what its row has shown: a final keeps its first text through corrections. */
  displayed: DisplayedText;
  /** The hangup wrap-up of an answered call that ended: the summary once written, and whether it is still awaited. */
  wrapup: WrapUpState;
  /** Set once the wrap-up box has said a wrap-up is coming for this call (review round 3): from then on it keeps
   * say-next's place, whatever the reads say next. */
  wrapupHold: WrapUpHold | null;
  /** This window's thumbs on the call shown, by `${target}:${target_id}`: a mirror of the host's CoachFeedbackVotes
   * (feedbackClient.ts), so the view's props stay a pure function of the snapshot. */
  votes: Readonly<Record<string, CoachFeedbackVote>>;
}

export interface DispositionState { selected: string | null; busy: string | null; status: string | null }
const NO_DISPOSITION: DispositionState = Object.freeze({ selected: null, busy: null, status: null });
/** until: off the coach's hours, the opening the wrap-up waits for (only present then). */
export interface WrapUpState { callId: string | null; summary: CallSummary | null; waiting: boolean; until?: string }
const NO_WRAPUP: WrapUpState = Object.freeze({ callId: null, summary: null, waiting: false });
/** The wrap-up box said a wrap-up is coming for callId ("Writing the wrap-up", "Wrap-up at 7:00", or a wrap-up), so it
 * never hands the box back to say-next for that call: a promise is not taken back on screen (review round 3, after the
 * box went say, wrap-up, say, wrap-up when the reads ran out before call.summary_ready). stalled: the read schedule ran
 * out while it was still being written; the box says "Wrap-up pending" until call.summary_ready reads it. */
export interface WrapUpHold { callId: string; stalled: boolean }
const NO_VOTES: Readonly<Record<string, CoachFeedbackVote>> = Object.freeze({});
/** After the call ends the summary job needs the transcript to settle and one model call: GET /summary is read again
 * at these delays (and at once on call.summary_ready) before the wrap-up stops saying it is being written. */
export const SUMMARY_RETRY_MS = [3000, 6000, 12000, 24000, 48000] as const;
export const DISPOSITION_SAVED = 'Saved to the CRM ledger';
export const DISPOSITION_FAILED = 'Couldn’t save it. Try again.';
/** A pending window that hears nothing for HELLO_TRIES hellos says the call didn't start (it binds if the call shows up). */
export const PENDING_TIMEOUT_MESSAGE = 'The call didn’t start';
/** Backoff for re-reading the session after a network error, and for reviving a stream the server closed. */
export const SESSION_RETRY_MS = [1000, 2000, 4000, 8000, 15000, 30000] as const;

type KeyStorage = Pick<Storage, 'getItem' | 'setItem'>;
type SessionRead = { kind: 'ok'; session: Session | null } | { kind: 'error' };
interface LockLike { request(name: string, options: { steal?: boolean }, callback: () => Promise<void>): Promise<unknown> }

export interface CallWindowHostDeps {
  target: CallWindowTarget;
  fetch?: typeof fetch;
  channel?: () => ChannelLike | null;
  /** Receive-only: the main app's session channel. */
  sessionChannel?: () => ChannelLike | null;
  history?: Pick<History, 'replaceState'> | null;
  storage?: () => KeyStorage | null;
  locks?: LockLike | null;
  now?: () => number;
  /** The window's own storage-event and pagehide hooks; omitted under the test runner. */
  events?: Pick<Window, 'addEventListener' | 'removeEventListener'> | null;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

const EMPTY_COACH: CoachState = { result: null, thinking: false, error: null };
const EMPTY_TRANSCRIPT: LiveTranscriptState = { transcript: null, loading: false, error: null, errorView: null, initialized: false };

/** The snapshot before anything has loaded: the full skeleton renders from it. */
export function initialCallWindowSnapshot(target: CallWindowTarget): CallWindowSnapshot {
  return {
    phase: 'booting', target, session: null, config: null, call: null, card: null, previous: null,
    stream: 'idle', failMessage: null, coachEnabled: true, coach: EMPTY_COACH, transcript: EMPTY_TRANSCRIPT,
    frozen: null, pin: null, askBusy: null, askStatus: null, askEntries: [], mediaReady: true, mediaEpoch: 1,
    disposition: NO_DISPOSITION, displayed: NO_DISPLAYED, wrapup: NO_WRAPUP, wrapupHold: null, votes: NO_VOTES,
  };
}

/** A CallSummary for this call (GET /calls/{id}/summary, docs/design/CALL-POP-KRISP-BAR.md section 3), or null. */
export function readCallSummary(body: unknown, callId: string): CallSummary | null {
  const d = (body as { data?: unknown } | null)?.data as Record<string, unknown> | null | undefined;
  if (!d || typeof d !== 'object' || d.call_id !== callId) return null;
  const text = (v: unknown, max: number) => typeof v === 'string' && v.length <= max;
  const time = (v: unknown) => typeof v === 'string' && Number.isFinite(Date.parse(v));
  const items = d.action_items;
  if (!Number.isInteger(d.version) || (d.version as number) < 1) return null;
  if (!Array.isArray(d.lines) || d.lines.length > 2 || !d.lines.every(l => text(l, 240))) return null;
  if (d.next_step !== null && !text(d.next_step, 300)) return null;
  if (d.next_step_at !== null && !time(d.next_step_at)) return null;
  if (!Array.isArray(items) || items.length > 6 || !items.every(i => !!i && typeof i === 'object' && text((i as Record<string, unknown>).text, 300)
    && ((i as Record<string, unknown>).owner === 'us' || (i as Record<string, unknown>).owner === 'them')
    && ((i as Record<string, unknown>).due === null || text((i as Record<string, unknown>).due, 40)))) return null;
  if (!(CALL_SUMMARY_OUTCOMES as readonly unknown[]).includes(d.outcome)) return null;
  if (d.suggested_disposition !== null && !text(d.suggested_disposition, 60)) return null;
  if (!(ASK_PROVIDERS as readonly unknown[]).includes(d.provider) || !text(d.model, 200)) return null;
  if (!Number.isInteger(d.segments_used) || (d.segments_used as number) < 0 || !time(d.computed_at)) return null;
  if (d.skipped_reason !== null && !(CALL_SUMMARY_SKIPPED_REASONS as readonly unknown[]).includes(d.skipped_reason)) return null;
  return d as unknown as CallSummary;
}

function browserSessionChannel(): ChannelLike | null {
  try { return typeof BroadcastChannel === 'undefined' ? null : (new BroadcastChannel('sidetone-session') as unknown as ChannelLike); } catch { return null; }
}
function browserStorage(): KeyStorage | null {
  try { return typeof window === 'undefined' ? null : window.localStorage; } catch { return null; }
}
function browserLocks(): LockLike | null {
  const locks = (globalThis.navigator as Navigator & { locks?: LockLike } | undefined)?.locks;
  return locks && typeof locks.request === 'function' ? locks : null;
}

export class CallWindowHost {
  readonly api: ApiClient;
  readonly mediaAccess = new MediaAccess();
  /** The window's one vote store (lane 6): shown at once, sent one at a time per target, a failure puts the accepted
   * vote back. Cleared for a call when the window moves to another. */
  readonly feedback: CoachFeedbackVotes;
  /** The targets voted on for the call shown, so the snapshot's mirror can be read back from the store. */
  private voted = new Map<string, { target: CoachFeedbackTarget; targetId: string }>();
  private readonly coachPoller: CoachPoller;
  private readonly transcriptPoller: LiveTranscriptPoller;
  private readonly asker: CoachAskClient;
  private state: CallWindowSnapshot;
  private readonly listeners = new Set<() => void>();
  private stream: EventStream | null = null;
  private chan: ChannelLike | null = null;
  private sessionChan: ChannelLike | null = null;
  private helloTimer: unknown = null;
  private cleanups: (() => void)[] = [];
  private running = false;
  /** Bumped by start and stop only: the Web Lock belongs to one run of the window, whatever it rebuilds in between. */
  private run = 0;
  /** Bumped by start, stop, sign-out and every rebuild: a boot or a call load begun under an older one is dropped. */
  private generation = 0;
  private callRequest = 0;
  private callApplied = 0;
  private coachKey = '';
  private transcriptKey = '';
  private lastLines: string[] | null = null;
  private lastState: Call['state'] | null = null;
  private askController: AbortController | null = null;
  private askSeq = 0;
  private summaryKey = '';
  private summaryTries = 0;
  private summaryTimer: unknown = null;
  private summaryController: AbortController | null = null;
  /** The wrap-up is asked for (POST) only when this rep had Coach on as the call ended; otherwise it is only read. */
  private summaryAsk = false;
  private dispositionController: AbortController | null = null;
  private dispositionAutomatic = false;
  private readonly automaticDispositionAttempts = new Set<string>();
  private readonly manualDispositionChoices = new Set<string>();
  private queued: unknown[] = [];
  private known = new Set<string>();
  private launchedAt = 0;
  /** The pending launch heard nothing for HELLO_TRIES hellos: it says the call didn't start, and still binds if it shows up. */
  private pendingTimedOut = false;
  private releaseLock: (() => void) | null = null;
  /** A newer call window took the Web Lock: this one leaves the coach alone until the lock comes back to it. */
  private lockLost = false;
  /** The signed-in half of the boot (config, stream, call) has run for the current session. */
  private booted = false;
  private sessionSeq = 0;
  private sessionRetry: unknown = null;
  private sessionFailures = 0;
  private reviveTimer: unknown = null;
  private revives = 0;
  private lastSignedOutCheck = Number.NEGATIVE_INFINITY;
  private readonly now: () => number;
  private readonly storage: () => KeyStorage | null;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;
  private readonly controlHandler: (event: { type: string; reason: string | null }) => Promise<void>;

  constructor(private readonly deps: CallWindowHostDeps) {
    this.now = deps.now ?? Date.now;
    this.storage = deps.storage ?? browserStorage;
    this.setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = deps.clearTimer ?? (handle => clearTimeout(handle as ReturnType<typeof setTimeout>));
    this.api = new ApiClient({
      getCsrfToken: () => this.state.session?.csrf_token ?? null,
      onUnauthenticated: () => { void this.recheckSession(); },
      ...(deps.fetch ? { fetch: deps.fetch } : {}),
    });
    // Exactly one of each for the window: the call window is the only coach poller (the dock no longer mounts one).
    this.coachPoller = new CoachPoller(this.api, this.mediaAccess);
    this.transcriptPoller = new LiveTranscriptPoller(this.api, this.mediaAccess, { includeEnded: true });
    this.asker = new CoachAskClient(this.api);
    this.feedback = new CoachFeedbackVotes(this.api);
    this.controlHandler = createControlHandler({
      access: this.mediaAccess,
      refreshSession: async () => !!(await this.recheckSession()),
      reloadConfig: () => { void this.loadConfig(); },
      onPolicyChanged: () => this.signedOut(),
    });
    this.state = initialCallWindowSnapshot(deps.target);
  }

  getSnapshot = (): CallWindowSnapshot => this.state;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  private set(patch: Partial<CallWindowSnapshot>): void {
    this.state = { ...this.state, ...patch };
    this.sync();
    for (const l of [...this.listeners]) l();
  }

  get account(): string | null {
    const s = this.state.session;
    return s?.user && s.tenant ? callWindowAccount(s.tenant.id, s.user.id) : null;
  }
  get userKey(): string | null {
    const s = this.state.session;
    return s?.user ? coachUserKey(s.tenant?.id, s.user.id) : null;
  }
  get selfUserId(): string | null { return this.state.session?.user?.id ?? null; }

  start(): void {
    if (this.running) return;
    this.running = true;
    const run = ++this.run;
    const generation = ++this.generation;
    this.cleanups.push(this.coachPoller.subscribe(() => this.onCoach()));
    this.cleanups.push(this.transcriptPoller.subscribe(() => {
      const transcript = this.transcriptPoller.snapshot();
      this.set({ transcript, displayed: nextDisplayedText(this.state.displayed, transcript.transcript) });
    }));
    this.cleanups.push(this.feedback.subscribe(() => this.mirrorVotes()));
    this.cleanups.push(this.mediaAccess.subscribe(() => {
      const snap = this.mediaAccess.getSnapshot();
      this.lastLines = null;
      this.set({ mediaReady: snap.state === 'ready', mediaEpoch: snap.epoch, frozen: null, pin: null });
    }));
    this.chan = (this.deps.channel ?? browserChannel)();
    if (this.chan) this.chan.onmessage = event => this.receive(event.data);
    this.sessionChan = (this.deps.sessionChannel ?? browserSessionChannel)();
    if (this.sessionChan) this.sessionChan.onmessage = () => { void this.recheckSession(); };
    const events = this.deps.events === undefined ? (typeof window === 'undefined' ? null : window) : this.deps.events;
    if (events) {
      const onStorage = (e: StorageEvent) => {
        if (e.key !== COACH_STORAGE_KEY && e.key !== null) return;
        const key = this.userKey;
        if (key) this.set({ coachEnabled: coachDefaultEnabled(this.storage(), key) });
      };
      const onHide = () => { const account = this.account; if (account) this.post({ v: 1, account, type: 'gone' }); };
      events.addEventListener('storage', onStorage as EventListener);
      events.addEventListener('pagehide', onHide);
      this.cleanups.push(() => { events.removeEventListener('storage', onStorage as EventListener); events.removeEventListener('pagehide', onHide); });
    }
    this.takeLock(run, true);
    void this.boot(generation);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    ++this.run;
    ++this.generation;
    this.stopHello();
    this.clearSessionRetry();
    this.clearRevive();
    this.stream?.stop();
    this.stream = null;
    this.coachPoller.stop();
    this.transcriptPoller.stop();
    this.stopSummary();
    this.summaryKey = '';
    this.askController?.abort();
    this.askController = null;
    this.dispositionController?.abort();
    this.dispositionController = null;
    for (const c of this.cleanups.splice(0)) c();
    try { this.chan?.close(); } catch { /* closed */ }
    try { this.sessionChan?.close(); } catch { /* closed */ }
    this.chan = null;
    this.sessionChan = null;
    this.releaseLock?.();
    this.releaseLock = null;
    this.lockLost = false;
    this.booted = false;
    this.coachKey = '';
    this.transcriptKey = '';
  }

  /**
   * The newest call window takes the lock with steal, so only one window polls the coach (GET /coach is 30 a minute
   * per user). A window the lock was stolen from queues for it again without steal: when the newer window closes, the
   * lock comes back and this window has the coach again, instead of saying "moved" for the rest of its life.
   */
  private takeLock(run: number, steal: boolean): void {
    const locks = this.deps.locks === undefined ? browserLocks() : this.deps.locks;
    if (!locks) return;
    const hold = () => new Promise<void>(release => {
      if (run !== this.run || !this.running) { release(); return; }
      this.releaseLock = release;
      if (this.lockLost) {
        this.lockLost = false;
        this.set(this.state.phase === 'moved' ? { phase: this.state.call ? 'ready' : 'booting' } : {});
      }
    });
    const lost = () => {
      if (run !== this.run || !this.running) return;
      this.releaseLock = null;
      this.lockLost = true;
      this.set(this.state.phase === 'ready' ? { phase: 'moved' } : {});
      this.takeLock(run, false);
    };
    try {
      if (steal) void locks.request(CALL_WINDOW_LOCK, { steal: true }, hold).catch(lost);
      else void locks.request(CALL_WINDOW_LOCK, {}, hold).catch(lost);
    } catch { /* No lock manager here: this window simply polls, as without Web Locks. */ }
  }

  private async boot(generation: number): Promise<void> {
    const read = await this.readSession();
    if (generation !== this.generation || !this.running) return;
    // The server could not be reached: stay on the skeleton and try again; the retry finishes the boot.
    if (read.kind === 'error') { this.retrySession(); return; }
    const session = read.session;
    if (!session?.user || !session.tenant) { this.set({ session, phase: 'signed_out' }); return; }
    await this.bootSignedIn(generation, session);
  }

  /** Config, the event stream, then the call (or the pending launch), then whatever the main window said meanwhile. */
  private async bootSignedIn(generation: number, session: Session): Promise<void> {
    this.booted = true;
    this.set({ session, coachEnabled: coachDefaultEnabled(this.storage(), coachUserKey(session.tenant?.id, session.user?.id ?? '')) });
    await this.loadConfig();
    if (generation !== this.generation || !this.running) return;
    this.startStream();
    const target = this.state.target;
    if (target.kind === 'call') await this.loadCall(target.callId, true);
    else this.beginPending(target.launch);
    if (generation !== this.generation) return;
    const queued = this.queued.splice(0);
    for (const m of queued) this.receive(m);
  }

  /** 'error' when the server could not be reached or failed: that is not a signed-out answer. */
  private async readSession(): Promise<SessionRead> {
    try {
      return { kind: 'ok', session: (await this.api.request<DataEnvelope<Session>>('/auth/session')).data ?? null };
    } catch (error) {
      if (isApiRequestError(error) && error.status === 401) return { kind: 'ok', session: null };
      return { kind: 'error' };
    }
  }

  /**
   * Another tab signed in, out or switched tenant, a request came back 401, or the stream closed. The same user carries
   * on (a stream the server closed starts again); a different user, or a sign-in after this window was signed out,
   * rebuilds the window in place; anyone signed out sees a fixed signed-out state with the transcript and coach
   * cleared. A network error changes nothing: it is retried with backoff. Only the latest check decides.
   */
  private async recheckSession(): Promise<Session | null> {
    const seq = ++this.sessionSeq;
    const read = await this.readSession();
    if (!this.running) return null;
    if (seq !== this.sessionSeq) return read.kind === 'ok' && read.session?.user && read.session.tenant ? read.session : null;
    if (read.kind === 'error') { this.retrySession(); return null; }
    this.clearSessionRetry();
    const session = read.session;
    if (!session?.user || !session.tenant) { this.signedOut(session); return null; }
    const before = this.state.session;
    const same = !!before?.user && before.user.id === session.user.id && before.tenant?.id === session.tenant.id;
    if (!this.booted || !same || this.state.phase === 'signed_out') { this.rebuild(session); return session; }
    this.set({ session });
    this.reviveStream();
    if (this.mediaAccess.getSnapshot().state === 'blocked') this.mediaAccess.ready();
    return session;
  }

  private retrySession(): void {
    if (this.sessionRetry !== null || !this.running) return;
    const ms = SESSION_RETRY_MS[Math.min(this.sessionFailures, SESSION_RETRY_MS.length - 1)]!;
    this.sessionFailures += 1;
    this.sessionRetry = this.setTimer(() => { this.sessionRetry = null; void this.recheckSession(); }, ms);
  }

  private clearSessionRetry(): void {
    if (this.sessionRetry !== null) this.clearTimer(this.sessionRetry);
    this.sessionRetry = null;
    this.sessionFailures = 0;
  }

  /** Signed in again (or as someone else): start over in place, as a fresh window on the same address would. */
  private rebuild(session: Session): void {
    const generation = ++this.generation;
    this.clearSessionRetry();
    this.clearRevive();
    this.stopHello();
    this.stream?.stop();
    this.stream = null;
    this.resetCall();
    this.known = new Set();
    this.booted = false;
    this.set({ session, phase: 'booting', coach: EMPTY_COACH, transcript: EMPTY_TRANSCRIPT, stream: 'idle' });
    this.mediaAccess.ready();
    void this.bootSignedIn(generation, session);
  }

  private signedOut(session: Session | null = null): void {
    // A boot or call load still in flight must not bring the call back on screen.
    ++this.generation;
    this.booted = false;
    this.clearSessionRetry();
    this.clearRevive();
    this.stream?.stop();
    this.stream = null;
    this.stopHello();
    this.coachPoller.stop();
    this.transcriptPoller.stop();
    this.askController?.abort();
    this.askController = null;
    this.dispositionController?.abort();
    this.dispositionController = null;
    this.mediaAccess.invalidate('blocked', 'session_expired');
    this.lastLines = null;
    this.coachKey = '';
    this.transcriptKey = '';
    this.stopSummary();
    this.summaryKey = '';
    this.voted.clear();
    this.feedback.clear();
    // No account while signed out: the main window's messages wait in the queue for the next sign-in instead of being
    // dropped as another account's.
    this.set({
      session, phase: 'signed_out', frozen: null, pin: null, askBusy: null, askEntries: [], askStatus: null,
      coach: EMPTY_COACH, transcript: EMPTY_TRANSCRIPT, disposition: NO_DISPOSITION, displayed: NO_DISPLAYED, wrapup: NO_WRAPUP, wrapupHold: null, votes: NO_VOTES,
    });
  }

  private async loadConfig(): Promise<void> {
    try {
      const env = await this.api.request<DataEnvelope<Config>>('/config');
      if (this.running && env?.data) this.set({ config: env.data });
    } catch { /* The coach and transcript stay unavailable until the next reload. */ }
  }

  private startStream(): void {
    if (this.stream) return;
    const stream = new EventStream({
      url: this.api.url('/events'),
      ...(this.deps.fetch ? { fetch: this.deps.fetch } : {}),
      onEvent: event => this.onEvent(event),
      onResync: () => {
        notifyTranscriptResync();
        const id = this.currentCallId();
        if (id) void this.loadCall(id, !this.state.call);
        void this.loadConfig();
      },
      onSessionExpired: () => { void this.recheckSession(); },
      onControl: control => { void this.controlHandler(control); },
      onStatus: status => {
        if (status.state === 'live') this.revives = 0;
        if (this.state.stream !== status.state) this.set({ stream: status.state });
      },
    });
    this.stream = stream;
    stream.start(null);
  }

  /**
   * The server closed the stream for good (401/403 on /events, or a session.expired control) but the session turned out
   * fine, for example because the main window signed in again: start it again from its cursor so Hold, Resume and the
   * end of the call keep arriving. Backs off if the server keeps closing it.
   */
  private reviveStream(): void {
    const stream = this.stream;
    if (!stream || this.reviveTimer !== null) return;
    const state = stream.currentStatus.state;
    if (state !== 'unauthenticated' && state !== 'stopped') return;
    const delay = this.revives === 0 ? 0 : SESSION_RETRY_MS[Math.min(this.revives - 1, SESSION_RETRY_MS.length - 1)]!;
    this.revives += 1;
    const go = () => {
      this.reviveTimer = null;
      if (this.running && this.stream === stream) stream.start(stream.currentCursor);
    };
    if (delay === 0) go();
    else this.reviveTimer = this.setTimer(go, delay);
  }

  private clearRevive(): void {
    if (this.reviveTimer !== null) this.clearTimer(this.reviveTimer);
    this.reviveTimer = null;
    this.revives = 0;
  }

  private onEvent(event: EventEnvelope): void {
    // The in-window bus the coach and transcript pollers listen to.
    notifyTranscriptEvent(event);
    const call = this.state.call;
    if (event.type === 'transcript.state_changed' && call && event.aggregate.id === call.id) {
      this.lastLines = null;
      if (this.state.frozen || this.state.pin) this.set({ frozen: null, pin: null });
    }
    if (event.type === 'call.summary_ready') {
      const id = (event.data as { call_id?: unknown } | undefined)?.call_id ?? event.aggregate.id;
      // The wrap-up was written: read it now instead of at the next retry.
      if (this.summaryKey && id === this.summaryKey) { this.stopSummary(); this.summaryTries = 0; void this.loadSummary(this.summaryKey); }
    }
    const decision = decideEvent(new Map(call ? [[call.id, call]] : []), event);
    if (decision.kind === 'refetch_call') {
      const shown = this.currentCallId();
      if (shown && decision.callId === shown) void this.loadCall(shown, !call);
      else if (this.awaitingBind() || call?.state === 'held') void this.considerCall(decision.callId);
    } else if (decision.kind === 'refetch_config') void this.loadConfig();
  }

  /** A pending window still waiting for its call ID (also after its hellos ran out with no answer). */
  private awaitingBind(): boolean {
    return this.state.target.kind === 'pending' && (this.state.phase === 'pending' || (this.state.phase === 'failed_start' && this.pendingTimedOut));
  }

  /** A call the window is not showing: bind a pending window to it, or switch to it during call waiting. */
  private async considerCall(id: string): Promise<void> {
    if (!isCallId(id) || this.known.has(id)) return;
    const generation = this.generation;
    let candidate: CallWithContext;
    try { candidate = (await this.api.request<DataEnvelope<CallWithContext>>(`/calls/${encodeURIComponent(id)}`)).data; } catch { return; }
    if (generation !== this.generation || !candidate) return;
    if (this.awaitingBind()) {
      if (selfSelectPending(candidate, this.selfUserId, this.launchedAt, this.known)) this.bindTo(candidate.id, candidate);
      else this.known.add(id);
      return;
    }
    if (pickCallWindowCall(this.state.call, candidate, this.selfUserId)) this.switchTo(candidate.id, candidate);
  }

  private async loadCall(id: string, initial: boolean): Promise<void> {
    const request = ++this.callRequest;
    const generation = this.generation;
    try {
      const env = await this.api.request<DataEnvelope<CallWithContext>>(`/calls/${encodeURIComponent(id)}`);
      if (generation !== this.generation || !this.running) return;
      this.applyCall(env.data, request);
    } catch (error) {
      if (generation !== this.generation || isAbortError(error)) return;
      const status = isApiRequestError(error) ? error.status : 0;
      if ((status === 404 || status === 403) && (initial || !this.state.call)) this.set({ phase: 'unavailable' });
    }
  }

  private applyCall(incoming: CallWithContext | undefined, request: number): void {
    if (!incoming || typeof incoming !== 'object' || incoming.id !== this.currentCallId()) return;
    const current = this.state.call;
    if (current && current.id === incoming.id && incoming.version === current.version && request < this.callApplied) return;
    this.callApplied = Math.max(this.callApplied, request);
    const map = upsertCall(new Map(current && current.id === incoming.id ? [[current.id, current]] : []), incoming);
    const call = map.get(incoming.id) ?? incoming;
    const card = newestCard(this.state.card, contextCardOf(incoming), contextCardOf(call));
    if (this.state.card && card && (this.state.card.candidate_id !== card.candidate_id || this.state.card.source.id !== card.source.id)) {
      this.dispositionController?.abort();
      this.dispositionController = null;
      this.dispositionAutomatic = false;
      this.state = { ...this.state, disposition: NO_DISPOSITION };
    }
    this.known.add(call.id);
    this.set({ call, card, phase: this.lockLost ? 'moved' : 'ready' });
    if (!card && !this.state.previous && cardExpected(call, capability(this.state.config, 'crm.sync'))) {
      const callId = call.id;
      void previousCards.load(callId, () => this.api.request<unknown>(previousCardPath(callId))).then(result => {
        if (this.running && this.state.call?.id === callId) this.set({ previous: result });
      });
    }
  }

  private currentCallId(): string | null {
    const t = this.state.target;
    return t.kind === 'call' ? t.callId : null;
  }

  // ---- Pending launch: the Dial click opened the window before the call had an ID ----

  private beginPending(launch: string): void {
    this.launchedAt = this.now();
    this.pendingTimedOut = false;
    this.set({ phase: 'pending', call: null, card: null, previous: null, failMessage: null, pin: null, frozen: null });
    this.stopHello();
    let tries = 0;
    const hello = () => {
      this.helloTimer = null;
      const account = this.account;
      const target = this.state.target;
      if (!account || target.kind !== 'pending' || target.launch !== launch) return;
      if (tries++ >= HELLO_TRIES) {
        // Nobody named the call: the refusal was missed or the main window went away. Say so in place; a bind or the
        // call turning up in the stream still binds the window.
        if (this.state.phase === 'pending') { this.pendingTimedOut = true; this.set({ phase: 'failed_start', failMessage: PENDING_TIMEOUT_MESSAGE }); }
        return;
      }
      this.post({ v: 1, account, type: 'hello', launch });
      this.helloTimer = this.setTimer(hello, HELLO_EVERY_MS);
    };
    hello();
  }

  private stopHello(): void {
    if (this.helloTimer !== null) this.clearTimer(this.helloTimer);
    this.helloTimer = null;
  }

  private post(message: unknown): void {
    try { this.chan?.postMessage(message); } catch { /* best effort */ }
  }

  private receive(raw: unknown): void {
    const message = readCallWindowMessage(raw);
    if (!message) return;
    const account = this.account;
    if (!account) {
      this.queued.push(raw);
      if (this.queued.length > 10) this.queued.shift();
      // The main window is signed in and talking to this window: see whether this window can be signed in again too.
      if (this.state.phase === 'signed_out' && this.now() - this.lastSignedOutCheck >= 2000) {
        this.lastSignedOutCheck = this.now();
        void this.recheckSession();
      }
      return;
    }
    if (message.account !== account) return;
    const target = this.state.target;
    switch (message.type) {
      case 'bind':
        if (target.kind === 'pending' && target.launch === message.launch) this.bindTo(message.callId);
        else if (target.kind === 'call' && target.callId === message.callId) this.post({ v: 1, account, type: 'bound', launch: message.launch, callId: message.callId });
        return;
      case 'failed':
        if (target.kind === 'pending' && target.launch === message.launch) {
          this.stopHello();
          this.pendingTimedOut = false;
          this.set({ phase: 'failed_start', failMessage: message.message });
        }
        return;
      case 'show':
        if (target.kind !== 'call' || target.callId !== message.callId) this.switchTo(message.callId);
        return;
      case 'pending':
        if (target.kind !== 'pending' || target.launch !== message.launch) this.switchToPending(message.launch);
        return;
      default:
        return;
    }
  }

  private bindTo(callId: string, preloaded?: CallWithContext): void {
    const target = this.state.target;
    const launch = target.kind === 'pending' ? target.launch : null;
    this.stopHello();
    this.pendingTimedOut = false;
    this.replace(callWindowPath({ callId }));
    this.set({ target: { kind: 'call', callId }, failMessage: null });
    const account = this.account;
    if (account && launch) this.post({ v: 1, account, type: 'bound', launch, callId });
    if (preloaded) this.applyCall(preloaded, ++this.callRequest);
    void this.loadCall(callId, true);
  }

  /** The same window shows another call (a second call, or call waiting): in place, no reload. */
  private switchTo(callId: string, preloaded?: CallWithContext): void {
    this.resetCall();
    this.replace(callWindowPath({ callId }));
    this.set({ target: { kind: 'call', callId }, phase: this.lockLost ? 'moved' : 'booting' });
    if (preloaded) this.applyCall(preloaded, ++this.callRequest);
    void this.loadCall(callId, true);
  }

  private switchToPending(launch: string): void {
    this.resetCall();
    this.replace(callWindowPath({ launch }));
    this.set({ target: { kind: 'pending', launch } });
    this.beginPending(launch);
  }

  private resetCall(): void {
    // Another call: the old call's votes go (the server keeps them; this window never shows them again).
    const old = this.state.call?.id;
    this.voted.clear();
    if (old) this.feedback.clear(old);
    this.askController?.abort();
    this.askController = null;
    this.dispositionController?.abort();
    this.dispositionController = null;
    this.lastLines = null;
    this.lastState = null;
    this.coachKey = '';
    this.transcriptKey = '';
    this.pendingTimedOut = false;
    this.coachPoller.stop();
    this.transcriptPoller.stop();
    this.stopSummary();
    this.summaryKey = '';
    this.set({
      call: null, card: null, previous: null, failMessage: null, frozen: null, pin: null, askBusy: null, askStatus: null, askEntries: [],
      disposition: NO_DISPOSITION, displayed: NO_DISPLAYED, wrapup: NO_WRAPUP, wrapupHold: null, votes: NO_VOTES,
    });
  }

  private replace(path: string): void {
    const history = this.deps.history === undefined ? (typeof window === 'undefined' ? null : window.history) : this.deps.history;
    try { history?.replaceState(null, '', path); } catch { /* the address bar keeps the old path; the window still switches */ }
  }

  // ---- Coach, transcript, Ask ----

  /** Starts or stops the pollers when what they depend on changes (never on every update: start() re-fetches). */
  private sync(): void {
    if (!this.running) return;
    const s = this.state;
    const call = s.call;
    const signedIn = s.phase !== 'signed_out' && s.phase !== 'unavailable';
    const perms = s.session?.permissions ?? [];
    const state = effectiveState(call, this.selfUserId);

    // Freeze the last lines on Hold; Resume or anything that withdraws input clears them.
    if (state !== this.lastState) {
      const wasActive = this.lastState === 'active';
      this.lastState = state;
      if (state === 'held' && wasActive && this.lastLines?.length) this.state = { ...this.state, frozen: this.lastLines };
      else if (state !== 'held' && this.state.frozen) this.state = { ...this.state, frozen: null };
      if (state !== 'active' && this.state.pin) this.state = { ...this.state, pin: null };
    }

    const transcriptVisible = signedIn && !!call && liveTranscriptVisible(capability(s.config, 'transcription.live'), perms, s.mediaReady);
    const tKey = call ? `${call.id}|${call.state}|${call.transcription_state}|${transcriptVisible}|${s.mediaEpoch}` : '';
    if (tKey !== this.transcriptKey) {
      this.transcriptKey = tKey;
      if (call && transcriptVisible) this.transcriptPoller.start(call, true);
      else this.transcriptPoller.stop();
    }

    const coachOn = signedIn && !this.lockLost && !!call && state === 'active' && s.coachEnabled
      && coachVisible(s.config?.capabilities['transcript.coach'] ?? null, perms, s.mediaReady);
    const cKey = call ? `${call.id}|${coachOn}|${s.mediaEpoch}|${this.userKey ?? ''}` : '';
    if (cKey !== this.coachKey) {
      this.coachKey = cKey;
      // A tip is asked for when the prospect's turn ends (a final from anyone but my own leg), never on my finals or
      // corrections, and never on a timer (docs/design/CALL-POP-KRISP-BAR.md lane 2).
      if (call && coachOn) this.coachPoller.start({ id: call.id, state: 'active' }, true, { isSelf: id => this.isSelfParticipant(id) });
      else this.coachPoller.stop();
    }

    // The wrap-up: an answered call that ended, read with the transcript grant.
    const wantSummary = signedIn && !!call && call.state === 'ended' && !!call.answered_at && perms.includes('transcripts:read');
    const sKey = wantSummary && call ? call.id : '';
    if (sKey !== this.summaryKey) {
      this.summaryKey = sKey;
      this.stopSummary();
      this.summaryTries = 0;
      if (sKey) {
        // Coach off: the transcript and card do not go to the coach's provider for a wrap-up either.
        this.summaryAsk = s.coachEnabled;
        // Nothing is promised before the server answers: say-next keeps "Call ended" until it says a wrap-up is written
        // or coming, so a server without wrap-ups (or a rep with Coach off) never sees "Writing the wrap-up" taken back.
        this.state = { ...this.state, wrapup: { callId: sKey, summary: null, waiting: false }, wrapupHold: null };
        void this.loadSummary(sKey);
      } else if (this.state.wrapup !== NO_WRAPUP || this.state.wrapupHold) this.state = { ...this.state, wrapup: NO_WRAPUP, wrapupHold: null };
    }
    this.maybeAutomaticDisposition();
  }

  /** My own leg (not an outside party sharing my user): its finals are my words, never the prospect's turn ending. */
  private isSelfParticipant(participantId: string | null): boolean {
    const self = this.selfUserId;
    if (!participantId || !self) return false;
    const p = this.state.call?.participants.find(x => x.id === participantId);
    return !!p && p.kind !== 'external' && p.user_id === self;
  }

  // ---- Wrap-up ----

  /**
   * One read of GET /calls/{id}/summary through WrapUp.tsx's loadWrapUp (lane 4): a summary is shown once it is this
   * call's in the contract's shape; still being written (404 summary_pending, the network, a server error) reads again
   * on the retry schedule; none coming (summary_not_found, summary_unconfigured, no grant) stops the waiting line.
   */
  private async loadSummary(callId: string): Promise<void> {
    const controller = new AbortController();
    this.summaryController = controller;
    const current = () => !controller.signal.aborted && this.running && this.summaryKey === callId;
    try {
      const read = await loadWrapUp(this.api, callId, controller.signal, this.summaryAsk);
      if (!current()) return;
      const summary = read.summary ? readCallSummary({ data: read.summary }, callId) : null;
      // A call the job will skip (under 30 seconds, nothing transcribed) promises nothing while it is written: say-next
      // keeps "Call ended" and the skip, when it lands, shows nothing (review round 3). A written one still shows.
      const quiet = !wrapUpExpected(this.state.call?.id === callId ? this.state.call : null);
      if (summary) this.setWrapUp({ callId, summary, waiting: false });
      else if (read.until) this.deferSummary(callId, read.until, quiet);
      else if (read.waiting || read.summary) { if (!quiet) this.markWaiting(callId); this.retrySummary(callId, read.retryInMs); }
      else this.stopWaiting(callId);
    } catch (error) {
      // Not an API answer at all (loadWrapUp rethrows those): read it again later.
      if (!current() || isAbortError(error)) return;
      this.retrySummary(callId);
    } finally {
      if (this.summaryController === controller) this.summaryController = null;
    }
  }

  /** Every wrap-up state change goes through here: once the box shows for this call (being written, or a wrap-up that
   * takes the box), wrapupHold keeps it there. */
  private setWrapUp(wrapup: WrapUpState, stalled = false): void {
    const held = wrapup.callId !== null && this.state.wrapupHold?.callId === wrapup.callId;
    const shows = wrapup.waiting || (wrapup.summary !== null && !wrapUpQuietSkip(wrapup.summary));
    const hold = wrapup.callId !== null && (held || shows) ? { callId: wrapup.callId, stalled: stalled && wrapup.summary === null } : null;
    this.set({ wrapup, wrapupHold: hold });
  }

  /** The server says it is being written (or answered in a way worth reading again): the box says so. */
  private markWaiting(callId: string): void {
    const w = this.state.wrapup;
    if (w.callId === callId && !w.waiting && !w.summary) this.setWrapUp({ callId, summary: null, waiting: true });
  }

  /** No (newer) wrap-up is coming, or the reads ran out (stalled): stop saying it is being written, keeping one already
   * shown. A box that already said one is coming keeps its place (wrapupHold); stalled, it says "Wrap-up pending". */
  private stopWaiting(callId: string, stalled = false): void {
    const shown = this.state.wrapup.callId === callId ? this.state.wrapup.summary : null;
    this.setWrapUp({ callId, summary: shown, waiting: false }, stalled);
  }

  /** Off the coach's hours the job writes it at the opening: the box says when, and the window reads it then (and at
   * once on call.summary_ready), instead of retrying now and giving up. quiet: a call the job will skip promises
   * nothing; it is only read again then. */
  private deferSummary(callId: string, until: string, quiet = false): void {
    if (!quiet) this.setWrapUp({ callId, summary: null, waiting: true, until });
    const ms = Math.max(SUMMARY_RETRY_MS[0], Date.parse(until) - Date.now() + SUMMARY_RETRY_MS[0]);
    if (this.summaryTimer !== null) this.clearTimer(this.summaryTimer);
    this.summaryTimer = this.setTimer(() => {
      this.summaryTimer = null;
      if (this.running && this.summaryKey === callId) { this.summaryTries = 0; void this.loadSummary(callId); }
    }, Number.isFinite(ms) ? Math.min(ms, 24 * 3600_000) : SUMMARY_RETRY_MS[0]);
  }

  private retrySummary(callId: string, atLeastMs: number | null = null): void {
    const step = SUMMARY_RETRY_MS[this.summaryTries];
    this.summaryTries += 1;
    if (step === undefined) { this.stopWaiting(callId, true); return; }
    const ms = Math.max(step, atLeastMs ?? 0);
    this.summaryTimer = this.setTimer(() => {
      this.summaryTimer = null;
      if (this.running && this.summaryKey === callId) void this.loadSummary(callId);
    }, ms);
  }

  private stopSummary(): void {
    this.summaryController?.abort();
    this.summaryController = null;
    if (this.summaryTimer !== null) this.clearTimer(this.summaryTimer);
    this.summaryTimer = null;
  }

  // ---- Thumbs ----

  /**
   * A thumbs vote on the tip in say-next, an answer in the question thread or the wrap-up (POST
   * /calls/:id/coach/feedback, one vote per user per target), through the window's CoachFeedbackVotes: shown at once,
   * sent once, and taken back if the save fails. The same vote again sends nothing. Ids come from feedbackClient.ts.
   */
  vote(target: CoachFeedbackTarget, targetId: string | null, vote: CoachFeedbackVote): void {
    const call = this.state.call;
    if (!call || !targetId || this.state.phase === 'signed_out' || this.state.phase === 'unavailable') return;
    this.voted.set(`${target}:${targetId}`, { target, targetId });
    // A failure is shown by the store putting the accepted vote back; nothing to report beyond that.
    this.feedback.vote(call.id, target, targetId, vote).catch(() => undefined);
  }

  /** The snapshot's votes for the call shown, read back from the store after every change. */
  private mirrorVotes(): void {
    const callId = this.state.call?.id ?? null;
    const votes: Record<string, CoachFeedbackVote> = {};
    if (callId) for (const [key, t] of this.voted) { const v = this.feedback.voted(callId, t.target, t.targetId); if (v) votes[key] = v; }
    const before = this.state.votes;
    const keys = Object.keys(votes);
    if (keys.length === Object.keys(before).length && keys.every(k => before[k] === votes[k])) return;
    this.set({ votes: keys.length ? votes : NO_VOTES });
  }

  private onCoach(): void {
    const coach = this.coachPoller.snapshot();
    const lines = coach.result?.lines ?? [];
    if (lines.length && effectiveState(this.state.call, this.selfUserId) === 'active') this.lastLines = [...lines];
    const patch: Partial<CallWindowSnapshot> = { coach };
    if (pinCleared(this.state.pin, coach.result)) patch.pin = null;
    // A new tip clears a quick action's status; the same tip dimmed (a correction, off hours) does not.
    const before = this.state.coach.result;
    const newTip = !!coach.result && lines.length > 0 && (!before || coach.result.computed_at !== before.computed_at || coach.result.segments_used !== before.segments_used);
    if (this.state.askStatus && newTip) patch.askStatus = null;
    this.set(patch);
  }

  /** One question at a time. A sayable answer to a pinned quick action holds the say-next slot. */
  ask(text: string): void {
    const question = cleanQuestion(text);
    const call = this.state.call;
    if (!question || !call || this.state.askBusy || effectiveState(call, this.selfUserId) !== 'active') return;
    // Asking is the coach: not while it is unavailable to this person (the view turns the box off too).
    if (!coachVisible(this.state.config?.capabilities['transcript.coach'] ?? null, this.state.session?.permissions ?? [], this.state.mediaReady)) return;
    // Unique beyond this window: an answer's thumbs are kept per user, call and id (answerFeedbackTargetId), so a
    // reopened window's first question must not reuse the id of an answer rated in an earlier one.
    const id = `cw-ask-${this.now().toString(36)}-${++this.askSeq}`;
    const controller = new AbortController();
    this.askController = controller;
    const callId = call.id;
    const epoch = this.state.mediaEpoch;
    // A pinned quick action ("Break the ice") answers in say-next, never as a thread row as well: the thread shows one
    // row, and a duplicate there would push the rep's own last answer out of view. Only an answer that cannot be pinned
    // lands in the thread, as a new row once it arrives.
    const pinned = PINNED_ACTIONS.has(question);
    this.set({ askBusy: question, askStatus: null, ...(pinned ? {} : { askEntries: coachQaReducer(this.state.askEntries, { type: 'asked', id, question }).slice(0, ASK_ENTRIES) }) });
    const current = () => !controller.signal.aborted && this.running && this.state.call?.id === callId && this.state.mediaEpoch === epoch;
    this.asker.ask(callId, question, controller.signal).then(
      body => {
        if (!current()) return;
        const answer = readCoachAskResponse(body);
        if (!answer) {
          this.set({ askBusy: null, askStatus: 'Coach couldn’t answer that', ...(pinned ? {} : { askEntries: coachQaReducer(this.state.askEntries, { type: 'failed', id, error: UNREADABLE_ANSWER }) }) });
          return;
        }
        const pin = pinFor(question, answer);
        if (pin) { this.set({ askBusy: null, pin, askStatus: null }); return; }
        const asked = pinned ? coachQaReducer(this.state.askEntries, { type: 'asked', id, question }).slice(0, ASK_ENTRIES) : this.state.askEntries;
        const entries = coachQaReducer(asked, { type: 'answered', id, answer });
        this.set({ askBusy: null, askEntries: entries, askStatus: pinned ? 'Coach couldn’t answer that' : null });
      },
      error => {
        if (!current() || isAbortError(error)) return;
        const view = coachAskErrorView(error);
        // A pinned quick action's failure is said in say-next's status line only.
        this.set({ askBusy: null, askStatus: pinned ? view.title : null, ...(pinned ? {} : { askEntries: coachQaReducer(this.state.askEntries, { type: 'failed', id, error: view }) }) });
      },
    ).finally(() => { if (this.askController === controller) this.askController = null; });
  }

  // ---- CRM disposition ----

  /**
   * The CRM card's one-click disposition, from the call window's CRM pane (the dock no longer shows the card during a
   * call). The same ledger write-back as the dock's card: POST /calls/:id/context/disposition, then the call is read
   * again so the card shows what the CRM now holds. One save at a time; a failure keeps the earlier choice.
   */
  recordDisposition(dispositionId: string): void {
    const call = this.state.call, card = this.state.card;
    if (!call || !card || !this.account || this.state.session?.view_as) return;
    const model = contextCardModel(card);
    if (model.kind !== 'candidate' || !model.dispositions.some(d => d.id === dispositionId)) return;
    this.manualDispositionChoices.add(`${this.account}:${call.id}`);
    // A person can override an automatic save even while its reply is delayed. The server serializes both writes
    // and never lets an automatic request overwrite a prior manual audit, including after this abort.
    if (this.dispositionAutomatic) {
      this.dispositionController?.abort();
      this.dispositionController = null;
      this.dispositionAutomatic = false;
      this.state = { ...this.state, disposition: { ...this.state.disposition, busy: null } };
    }
    this.saveDisposition(dispositionId);
  }

  private maybeAutomaticDisposition(): void {
    const { call, card, session, wrapup, disposition } = this.state;
    const account = this.account;
    if (!this.running || this.state.phase !== 'ready' || !account || session?.view_as || !call || call.state !== 'ended'
      || !call.answered_at || !card || !this.state.mediaReady || !session?.permissions.includes('calls:control_own')
      || !session.permissions.includes('transcripts:read') || !call.participants.some(p => p.user_id === this.selfUserId)
      || disposition.selected || disposition.busy || wrapup.callId !== call.id || wrapup.summary?.call_id !== call.id) return;
    const key = `${account}:${call.id}`;
    if (this.automaticDispositionAttempts.has(key) || this.manualDispositionChoices.has(key)) return;
    const model = contextCardModel(card);
    if (model.kind !== 'candidate' || !model.candidateId) return;
    const id = automaticDispositionFor(wrapup.summary, model.dispositions);
    if (!id) return;
    this.automaticDispositionAttempts.add(key);
    this.saveDisposition(id, { summary_version: wrapup.summary.version, context_version: card.version });
  }

  private saveDisposition(dispositionId: string, automatic?: AutomaticDisposition): void {
    const call = this.state.call;
    const card = this.state.card;
    if (!call || !card || this.state.disposition.busy || this.state.phase !== 'ready' || this.state.session?.view_as) return;
    const model = contextCardModel(card);
    if (model.kind !== 'candidate' || !model.dispositions.some(d => d.id === dispositionId)) return;
    const spec = dispositionRequest(call.id, dispositionId, model.candidateId);
    const callId = call.id, account = this.account, generation = this.generation, sourceId = card.source.id;
    const before = this.state.disposition.selected;
    const controller = new AbortController();
    this.dispositionController = controller;
    this.dispositionAutomatic = !!automatic;
    this.set({ disposition: { selected: automatic ? before : spec.body.disposition, busy: dispositionId, status: null } });
    const current = () => !controller.signal.aborted && this.running && this.generation === generation && this.account === account
      && this.state.call?.id === callId && this.state.card?.source.id === sourceId && this.state.card?.candidate_id === card.candidate_id;
    this.api.request<DataEnvelope<CallDispositionReceipt>>(spec.path, { method: spec.method,
      body: JSON.stringify({ ...spec.body, ...(automatic ? { automatic } : {}) }), signal: controller.signal }).then(
      env => {
        if (!current()) return;
        const receipt = env.data;
        if (automatic && (!receipt || receipt.call_id !== callId || receipt.candidate_id !== model.candidateId
          || !['applied', 'already_recorded'].includes(receipt.automatic_status ?? '') || typeof receipt.disposition !== 'string')) {
          this.set({ disposition: { selected: before, busy: null, status: DISPOSITION_FAILED } });
          return;
        }
        this.set({ disposition: { selected: automatic ? receipt.disposition : spec.body.disposition, busy: null,
          status: automatic && receipt.automatic_status === 'applied' ? 'Automatically tagged and saved to the CRM ledger' : DISPOSITION_SAVED } });
        void this.loadCall(callId, false);
      },
      error => {
        if (!current() || isAbortError(error)) return;
        this.set({ disposition: { selected: before, busy: null, status: automatic ? 'Automatic tag could not be saved. Choose a tag to retry.' : DISPOSITION_FAILED } });
      },
    ).finally(() => { if (this.dispositionController === controller) { this.dispositionController = null; this.dispositionAutomatic = false; } });
  }

}

function pinFor(question: string, answer: CoachAskAnswer): SayPin | null {
  if (!PINNED_ACTIONS.has(question) || answer.state !== 'answered' || !answer.suggested_wording || isRepOnlyQuickAction(question)) return null;
  return { label: question, line1: answer.suggested_wording, line2: answer.suggestion, segmentsUsed: answer.segments_used };
}

function newestCard(...cards: (CallContextCard | null)[]): CallContextCard | null {
  let best: CallContextCard | null = null;
  for (const c of cards) if (c && (!best || c.version > best.version)) best = c;
  return best;
}
